import type { McpServerDto, Role, ToolDto } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { McpJsonImportDialog } from '@/components/mcp-json-import';
import { McpServerFormDialog, McpTestDialog, mcpTarget } from '@/components/mcp-server-dialogs';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { ToolFormDialog, toolReadOnlyReason } from '@/components/tool-form-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, errorMessage } from '@/lib/api';

const toolKindLabel = (kind: ToolDto['kind']) =>
  kind === 'http' ? 'HTTP' : kind === 'cli' ? 'CLI' : 'Sandbox';

const toolCall = (t: ToolDto) =>
  'method' in t.config
    ? `${t.config.method} ${t.config.urlTemplate}`
    : 'command' in t.config
      ? `${t.config.command} ${t.config.argsTemplate.join(' ')}`
      : 'Runs the model’s code in a sandbox';

/** Edit (or view, when read-only) and delete buttons for one row. */
function RowActions({
  name,
  readOnly,
  readOnlyReason,
  canDelete,
  deleting,
  onEdit,
  onDelete,
  children,
}: {
  name: string;
  readOnly: boolean;
  readOnlyReason: string | null;
  canDelete: boolean;
  deleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        size="icon-sm"
        variant="ghost"
        aria-label={readOnly ? `View ${name}` : `Edit ${name}`}
        title={readOnly ? (readOnlyReason ?? 'View') : 'Edit'}
        onClick={onEdit}
      >
        {readOnly ? <Eye aria-hidden /> : <Pencil aria-hidden />}
      </Button>
      {children}
      {canDelete && (
        <Button
          size="icon-sm"
          variant="ghost-destructive"
          aria-label={`Delete ${name}`}
          title="Delete"
          disabled={deleting}
          onClick={onDelete}
        >
          <Trash2 aria-hidden />
        </Button>
      )}
    </div>
  );
}

function ToolsTab({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const tools = useQuery({
    queryKey: ['tools', tenantId],
    queryFn: () => api<{ items: ToolDto[] }>('/tools'),
  });
  const items = tools.data?.items ?? [];
  const search = useListSearch(items, (t) => [t.name, t.description, toolKindLabel(t.kind)]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editing = items.find((t) => t.id === editingId) ?? null;
  const remove = useMutation({
    mutationFn: (tool: ToolDto) => api<void>(`/tools/${tool.id}`, { method: 'DELETE' }),
    onSuccess: (_, tool) => {
      toast.success(`${tool.name} deleted`);
      void queryClient.invalidateQueries({ queryKey: ['tools', tenantId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <ListSearch
        label="Search tools"
        placeholder="Search tools by name or description"
        value={search.query}
        onChange={search.setQuery}
        actions={
          canEdit && (
            <ToolFormDialog
              tenantId={tenantId}
              tool={null}
              open={createOpen}
              onOpenChange={setCreateOpen}
              trigger={<Button>New tool</Button>}
            />
          )
        }
      />
      <ErrorAlert error={tools.error} />
      {!tools.isPending && items.length === 0 ? (
        <EmptyState title="No tools yet">
          Create an HTTP tool, then attach it to an agent on its Tools &amp; MCP tab.
        </EmptyState>
      ) : items.length > 0 && search.filtered.length === 0 ? (
        <NoMatches query={search.query} />
      ) : (
        items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Calls</TableHead>
                <TableHead className="w-0">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((t) => {
                const reason = toolReadOnlyReason(t);
                return (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono font-medium">{t.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[28rem] truncate">
                      {t.description}
                    </TableCell>
                    <TableCell>
                      <span className="flex gap-1">
                        <Badge variant="secondary">{toolKindLabel(t.kind)}</Badge>
                        {t.pluginId && <Badge variant="outline">Plugin</Badge>}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-[22rem] truncate font-mono text-xs">
                      {toolCall(t)}
                    </TableCell>
                    <TableCell>
                      <RowActions
                        name={t.name}
                        readOnly={!canEdit || reason !== null}
                        readOnlyReason={reason}
                        canDelete={canEdit && !t.pluginId}
                        deleting={remove.isPending && remove.variables?.id === t.id}
                        onEdit={() => {
                          setEditingId(t.id);
                          setEditOpen(true);
                        }}
                        onDelete={() => remove.mutate(t)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )
      )}
      <ToolFormDialog
        tenantId={tenantId}
        tool={editing}
        readOnly={!canEdit || (editing !== null && toolReadOnlyReason(editing) !== null)}
        open={editOpen && editing !== null}
        onOpenChange={setEditOpen}
      />
    </div>
  );
}

function McpServersTab({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const servers = useQuery({
    queryKey: ['mcp-servers', tenantId],
    queryFn: () => api<{ items: McpServerDto[] }>('/mcp-servers'),
  });
  const items = servers.data?.items ?? [];
  const search = useListSearch(items, (s) => [s.name, s.description, mcpTarget(s)]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editing = items.find((s) => s.id === editingId) ?? null;
  const remove = useMutation({
    mutationFn: (server: McpServerDto) =>
      api<void>(`/mcp-servers/${server.id}`, { method: 'DELETE' }),
    onSuccess: (_, server) => {
      toast.success(`${server.name} removed`);
      void queryClient.invalidateQueries({ queryKey: ['mcp-servers', tenantId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-4">
      <ListSearch
        label="Search MCP servers"
        placeholder="Search MCP servers by name, description or target"
        value={search.query}
        onChange={search.setQuery}
        actions={
          canEdit && (
            <>
              <McpJsonImportDialog tenantId={tenantId} />
              <McpServerFormDialog
                tenantId={tenantId}
                server={null}
                open={createOpen}
                onOpenChange={setCreateOpen}
                trigger={<Button>New MCP server</Button>}
              />
            </>
          )
        }
      />
      <ErrorAlert error={servers.error} />
      {!servers.isPending && items.length === 0 ? (
        <EmptyState title="No MCP servers yet">
          Connect a server over HTTP or stdio, or import the mcp.json you already use in Claude
          Desktop, Claude Code, Cursor or VS Code.
        </EmptyState>
      ) : items.length > 0 && search.filtered.length === 0 ? (
        <NoMatches query={search.query} />
      ) : (
        items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Transport</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="w-0">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((s) => {
                const reason = s.pluginId
                  ? 'Installed by a plugin. Change it by updating the plugin.'
                  : null;
                return (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono font-medium">{s.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[24rem] truncate">
                      {s.description || '—'}
                    </TableCell>
                    <TableCell>
                      <span className="flex gap-1">
                        <Badge variant="secondary">
                          {s.transport === 'stdio' ? 'stdio' : 'HTTP'}
                        </Badge>
                        {s.pluginId && <Badge variant="outline">Plugin</Badge>}
                      </span>
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground max-w-[22rem] truncate font-mono text-xs"
                      title={mcpTarget(s)}
                    >
                      {mcpTarget(s)}
                    </TableCell>
                    <TableCell>
                      <RowActions
                        name={s.name}
                        readOnly={!canEdit || reason !== null}
                        readOnlyReason={reason}
                        canDelete={canEdit && !s.pluginId}
                        deleting={remove.isPending && remove.variables?.id === s.id}
                        onEdit={() => {
                          setEditingId(s.id);
                          setEditOpen(true);
                        }}
                        onDelete={() => remove.mutate(s)}
                      >
                        {canEdit && <McpTestDialog server={s} />}
                      </RowActions>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )
      )}
      <McpServerFormDialog
        tenantId={tenantId}
        server={editing}
        readOnly={!canEdit || Boolean(editing?.pluginId)}
        open={editOpen && editing !== null}
        onOpenChange={setEditOpen}
      />
    </div>
  );
}

function ToolsContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  return (
    <>
      <PageHeader
        title="Tools & MCP"
        description="Give agents abilities: HTTP and command-line tools, and tools from MCP servers."
      />
      <Tabs defaultValue="tools">
        <TabsList>
          <TabsTrigger value="tools">Tools</TabsTrigger>
          <TabsTrigger value="mcp">MCP servers</TabsTrigger>
        </TabsList>
        <TabsContent value="tools">
          <ToolsTab tenantId={tenantId} canEdit={canEdit} />
        </TabsContent>
        <TabsContent value="mcp">
          <McpServersTab tenantId={tenantId} canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </>
  );
}

export function ToolsPage() {
  return (
    <RequireTenant>
      {(tenant) => <ToolsContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
